import { type MessageKeys, useTranslations } from 'next-intl';
import { SITE } from '@/app/constants';
import type { StatConfig, ViduV1UserStat } from './types';

export const PAGE_SIZE = 10;
export const GUEST_PROFILE_PREVIEW_PAGE_SIZE = 3;

export const shouldLimitGuestProfileModules = (userId?: string) => SITE === 'china' && !!userId;

export enum PROFILE_TAB_ENUM {
  PUBLISHED = 'published',
  FAVORITE = 'likes',
  COLLECT = 'collected',
}

/** Secondary filter on published submissions */
export enum PUBLISHED_ACTIVITY_FILTER {
  ALL = 'all',
  ACTIVITY = 'activity',
  NON_ACTIVITY = 'non_activity',
}

export type PublishedActivityFilter = `${PUBLISHED_ACTIVITY_FILTER}`;

export const publishedActivityFilterToQuery = (filter: PublishedActivityFilter): boolean | undefined => {
  if (filter === PUBLISHED_ACTIVITY_FILTER.ACTIVITY) return true;
  if (filter === PUBLISHED_ACTIVITY_FILTER.NON_ACTIVITY) return false;
  return undefined;
};

export const PUBLISHED_ACTIVITY_FILTER_OPTIONS = () => {
  const t = useTranslations('profile');
  return [
    { key: PUBLISHED_ACTIVITY_FILTER.ALL, label: t('activityFilter.all') },
    { key: PUBLISHED_ACTIVITY_FILTER.ACTIVITY, label: t('activityFilter.activity') },
    { key: PUBLISHED_ACTIVITY_FILTER.NON_ACTIVITY, label: t('activityFilter.nonActivity') },
  ];
};

export enum COMMUNITY_SEARCH_OPTIONS {
  ALL = 'all',
  INSPIRATION = 'media_asset',
  SHORT_FILM = 'short_film',
  TUTORIAL = 'tutorial',
  REFERENCE = 'share_element',
}

export const COLLECT_EVENT_TABS_TYPE = {
  [COMMUNITY_SEARCH_OPTIONS.INSPIRATION]: 'inspiration_interaction',
  [COMMUNITY_SEARCH_OPTIONS.SHORT_FILM]: 'film_interaction',
  [COMMUNITY_SEARCH_OPTIONS.TUTORIAL]: 'tutorials_interaction',
};

export const LIKES_SUBTYPE: Record<COMMUNITY_SEARCH_OPTIONS, string> = {
  [COMMUNITY_SEARCH_OPTIONS.ALL]: 'all',
  [COMMUNITY_SEARCH_OPTIONS.INSPIRATION]: 'inspiration',
  [COMMUNITY_SEARCH_OPTIONS.SHORT_FILM]: 'shortfilm',
  [COMMUNITY_SEARCH_OPTIONS.TUTORIAL]: 'turtoials',
  [COMMUNITY_SEARCH_OPTIONS.REFERENCE]: 'references',
};

export const PROFILE_SEARCH_OPTIONS = () => {
  const t = useTranslations('creations');
  return [
    {
      key: COMMUNITY_SEARCH_OPTIONS.ALL,
      label: t('search.like.all'),
    },
    {
      key: COMMUNITY_SEARCH_OPTIONS.INSPIRATION,
      label: t('search.like.inspiration'),
    },
    {
      key: COMMUNITY_SEARCH_OPTIONS.SHORT_FILM,
      label: t('search.like.shortFilm'),
    },
    {
      key: COMMUNITY_SEARCH_OPTIONS.TUTORIAL,
      label: t('search.like.tutorial'),
    },
    {
      key: COMMUNITY_SEARCH_OPTIONS.REFERENCE,
      label: t('search.like.reference'),
    },
  ];
};

export const STAT_CONFIGS = ({
  handleClickStat,
}: {
  handleClickStat: (key: keyof ViduV1UserStat) => void;
}): StatConfig[] => {
  const statConfigs: StatConfig[] = [
    {
      key: 'inspiration_like_count',
      langKey: 'profile.likes',
    },
    {
      key: 'inspiration_recreate_count',
      langKey: 'profile.inspires',
    },
    {
      key: 'following_count',
      langKey: 'profile.following',
      onClick: () => handleClickStat('following_count'),
    },
    {
      key: 'follower_count',
      langKey: 'profile.followers',
      onClick: () => handleClickStat('follower_count'),
    },
  ];

  return statConfigs.filter((config) => {
    if (SITE !== 'china') return true;

    return !['inspiration_like_count', 'following_count', 'follower_count'].includes(config.key);
  });
};

export const STAT_MODAL_CONFIGS: {
  label: MessageKeys<IntlMessages, 'profile.following' | 'profile.followers'>;
  value: keyof ViduV1UserStat;
}[] = [
  {
    label: 'profile.following',
    value: 'following_count',
  },
  {
    label: 'profile.followers',
    value: 'follower_count',
  },
];

export const DEFAULT_SHOWN_MEDAL_IDS = [
  'zongmen_mvp',
  'zongmen_winner',
  'zongmen_duocan_participant',
  'zongmen_shouwei_participant',
  'anniversary_voyager',
  'anniversary_pro',
];
